import { Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  BellNotification,
  humanizeMasterType,
  isAbsoluteNotificationLink,
  mapBellApiDtoToBellNotification,
  resolveBellColor,
  resolveBellIcon
} from './notification-bell.interface';
import { NotificationBellService } from './notification-bell.service';
import { PushNotificationService } from 'src/app/Common/PushNotificationService/pushnotification.service';

const HISTORY_PAGE_SIZE = 10; // matches the dropdown's own page size -- fewer round-trips per scroll, still small enough to feel responsive

/**
 * In-app notification bell -- sits at the right end of the breadcrumb row (home.component.html
 * .topbar-breadcrumb) and again in the mobile navbar next to search. Both instances share one
 * poll/unread-state via NotificationBellService (see that file's class doc).
 *
 * Backed by ann.NotificationMaster_GetBellNotificationsByUserId (dropdown = latest unread,
 * "Show All" modal = full history, paginated via infinite scroll, split Unread-first / Read
 * client-side -- see unreadHistory/readHistory getters) and ann.NotificationMaster_MarkAsRead
 * (per-row dismiss + Clear All + click-to-open).
 */
@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css']
})
export class NotificationBellComponent implements OnInit, OnDestroy {

  @ViewChild('showAllModal', { static: true }) showAllModal!: TemplateRef<any>;

  private modalService = inject(NgbModal);
  private elementRef = inject(ElementRef);
  private bellService = inject(NotificationBellService);
  private pushNotificationService = inject(PushNotificationService);
  private router = inject(Router);
  private modalRef?: NgbModalRef;
  private subscriptions = new Subscription();

  isOpen = false;

  /** Dropdown list -- unread only, kept in sync via the shared service. */
  notifications: BellNotification[] = [];
  unreadCount = 0;
  /** True while the dropdown's own infinite scroll is fetching the next page -- see
   *  NotificationBellService.loadMoreUnread(). Drives the skeleton row at the bottom of the list. */
  dropdownLoading = false;
  /** True once every unread notification has been loaded into the dropdown -- drives the
   *  "You're all caught up" footer shown below a non-empty list (distinct from the big empty
   *  state, which only covers an empty list from the start). */
  dropdownExhausted = false;

  /** "Show All" modal list -- full history, loaded page-by-page as the user scrolls; split
   *  into the two getters below for the Unread-first / Read layout. */
  history: BellNotification[] = [];
  private historyPageNo = 1;
  private historyTotalCount = 0;
  historyLoading = false;
  historyExhausted = false;

  get unreadBadgeLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  get unreadHistory(): BellNotification[] {
    return this.history.filter(n => n.readStatus === 'Unread');
  }

  get readHistory(): BellNotification[] {
    return this.history.filter(n => n.readStatus === 'Read');
  }

  ngOnInit(): void {
    this.subscriptions.add(this.bellService.unread$.subscribe(items => this.notifications = items));
    this.subscriptions.add(this.bellService.unreadTotalCount$.subscribe(count => this.unreadCount = count));
    this.subscriptions.add(this.bellService.unreadLoading$.subscribe(loading => this.dropdownLoading = loading));
    this.subscriptions.add(this.bellService.unreadExhausted$.subscribe(exhausted => this.dropdownExhausted = exhausted));
    // Lets something outside the bell (the in-app toast, for an aggregated push or an
    // away/login summary -- none of which have one single destination to navigate to) ask for
    // the dropdown to be opened instead. See NotificationBellService.requestOpenDropdown().
    this.subscriptions.add(this.bellService.openDropdownRequested$.subscribe(() => this.isOpen = true));
    this.bellService.startPolling();
    // Re-establishes push registration + the live onMessage/service-worker wiring for an
    // already-authenticated session that didn't just log in (a page refresh, or a tab that's
    // simply been open since before this wiring existed) -- see the method's doc comment.
    this.pushNotificationService.registerCurrentDeviceFromSession();
    // Away catch-up + login summary toasts -- independent of push/Firebase being configured at
    // all, since both are driven off the bell's own unread-count endpoint. See the method's doc
    // comment on PushNotificationService for how the two are told apart.
    this.pushNotificationService.wireCatchUpNotifications();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  /** Closes the dropdown on an outside click, same pattern as a standard Bootstrap dropdown. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  /** Cross icon on a single row -- marks it read only, no navigation. */
  dismiss(notification: BellNotification, event: Event): void {
    event.stopPropagation();
    this.markRead(notification);
  }

  /**
   * Clicking anywhere else on a row -- only notifications that carry a click-through link
   * (AttachmentPath, same convention as the FCM push click handler in
   * firebase-messaging-sw.js) are interactive at all; a row without one does nothing here
   * (its cursor is also reset to default in CSS so it doesn't look clickable). When there
   * is a link, mark it read (if it wasn't already), navigate there, and close whichever
   * container (dropdown or modal) triggered it.
   */
  openNotification(notification: BellNotification): void {
    if (!notification.attachmentPath) {
      return;
    }
    if (notification.readStatus === 'Unread') {
      this.markRead(notification);
    }
    this.closeDropdown();
    this.closeShowAllModal();
    // A real URL (http://... -- see isAbsoluteNotificationLink's doc comment) needs an actual
    // browser navigation; router.navigateByUrl() would otherwise mangle it into a broken in-app
    // route. New tab so the ERP session/state in this tab isn't lost by leaving the SPA.
    if (isAbsoluteNotificationLink(notification.attachmentPath)) {
      window.open(notification.attachmentPath, '_blank', 'noopener');
    } else {
      this.router.navigateByUrl(notification.attachmentPath);
    }
  }

  /** Footer "Clear All" -- marks every currently-unread row as read; history entries flip to Read too. */
  clearAll(event: Event): void {
    event.stopPropagation();
    this.bellService.clearAll().subscribe({
      error: err => {
        console.error('[NotificationBell] Failed to clear all notifications -- resyncing.', err);
        this.bellService.refreshUnread();
      }
    });
    this.history.forEach(n => { if (n.readStatus === 'Unread') { n.readStatus = 'Read'; } });
  }

  openShowAll(event: Event): void {
    event.stopPropagation();
    this.closeDropdown();
    this.history = [];
    this.historyPageNo = 1;
    this.historyTotalCount = 0;
    this.historyExhausted = false;
    this.loadNextHistoryPage();

    this.modalRef = this.modalService.open(this.showAllModal, {
      size: 'lg',
      scrollable: true,
      centered: true,
      ariaLabelledBy: 'notificationBellModalTitle'
    });
  }

  closeShowAllModal(): void {
    this.modalRef?.dismiss();
  }

  /** Bound to the dropdown list's (scroll) event -- infinite scroll for the small bell panel. */
  onDropdownScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 40;
    if (nearBottom) {
      this.bellService.loadMoreUnread();
    }
  }

  /** Bound to the modal body's (scroll) event -- infinite scroll for "Show All". */
  onHistoryScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;
    if (nearBottom) {
      this.loadNextHistoryPage();
    }
  }

  private loadNextHistoryPage(): void {
    if (this.historyLoading || this.historyExhausted) { return; }
    this.historyLoading = true;

    this.bellService.getHistoryPage(this.historyPageNo, HISTORY_PAGE_SIZE).subscribe({
      next: res => {
        const items = (res.items ?? []).map(mapBellApiDtoToBellNotification);
        this.history = [...this.history, ...items];
        this.historyTotalCount = items.length > 0 ? items[0].totalCount : this.historyTotalCount;
        this.historyPageNo++;
        this.historyExhausted = items.length < HISTORY_PAGE_SIZE || this.history.length >= this.historyTotalCount;
        this.historyLoading = false;
      },
      error: err => {
        console.error('[NotificationBell] Failed to load notification history:', err);
        this.historyLoading = false;
      }
    });
  }

  resolveIcon(masterType?: string | null): string {
    return resolveBellIcon(masterType);
  }

  resolveColor(masterType?: string | null): string {
    return resolveBellColor(masterType);
  }

  /** Soft (15%-opacity-ish) background tint behind the icon, derived from its resolved color. */
  resolveIconBackground(masterType?: string | null): string {
    return resolveBellColor(masterType) + '1f';
  }

  moduleLabel(masterType?: string | null): string {
    return humanizeMasterType(masterType);
  }

  /** "3 min ago" / "2 hr ago" / "5 days ago" -- no external date library needed for this. */
  timeAgo(date: Date): string {
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) { return 'Just now'; }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) { return `${minutes} min ago`; }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) { return `${hours} hr ago`; }
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  /** Marks read on the server (optimistic, via the shared service) and reflects it locally
   *  in both lists this notification might currently appear in. */
  private markRead(notification: BellNotification): void {
    this.bellService.dismiss(notification.notificationId).subscribe({
      error: err => {
        console.error('[NotificationBell] Failed to mark notification as read -- resyncing.', err);
        this.bellService.refreshUnread();
      }
    });
    notification.readStatus = 'Read';
    this.updateHistoryReadStatus(notification.notificationId);
  }

  private updateHistoryReadStatus(notificationId: number): void {
    const match = this.history.find(n => n.notificationId === notificationId);
    if (match) { match.readStatus = 'Read'; }
  }
}
